import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import PaginatedProjectPreview from './PaginatedProjectPreview';
import './ProposalGenerator.css';

const COMPANY = {
    name: 'BHOOMI TECHZONE PRIVATE LIMITED',
    shortName: 'Bhoomi Techzone Pvt. Ltd.',
    website: 'www.bhoomitechzone.in',
    email: 'bhoomi.sales@bhoomitechzone.in',
    gstin: '09AAMCB0572D1ZS',
    cin: 'U55499UP2023PTC191784',
    address: 'A-43, 3rd Floor, Sector 63, Noida (201301), Uttar Pradesh',
    phone: '',
    bankName: 'Punjab & Sind Bank',
    accountName: 'BHOOMI TECHZONE PRIVATE LIMITED',
    accountNumber: '15111100000386',
    ifsc: 'PSIB0021511',
    upi: 'bhoomitechzone@okaxis',
};

const SERVICES = [
    'Website Development', 'Web Portals Development', 'Software Development',
    'Android Application Development', 'Mobile Apps Development', 'Digital Marketing',
    'Graphics Designing', 'Promotional Video', 'Logo & Banner Designing',
    'Content Writing & Hosting Provider', 'ERP, CRM Development', 'MLM Website/App Development',
    'E-Commerce Development', 'Desktop Software Development', 'Taxi Booking Apps',
    'Food Delivery Apps', 'Service Provider Apps', 'Tournament/Contest Apps',
    'Real Estate Software', 'Pathology Software', 'Hospital Software',
    'Small Business Software', 'Billing Software', 'Examination Software',
    'Education Portals', 'Hotels Management Software', 'School Management Software',
];

const PROFILE_TEXT = `At BHOOMI TECHZONE, we specialize in creating beautiful websites that enhance your brand's online presence and establish a standard for innovative design in the industry. Our expertise extends beyond design — we develop a comprehensive digital ecosystem customized for your business. From top-notch SEO strategies to targeted advertising campaigns, we make sure your audience can easily find you and engage with your content meaningfully. Our mission is straightforward: to help your business succeed online by combining aesthetics, functionality, and results-driven digital marketing solutions.`;

export function DomesticProposalGenerator({ isOpen, onClose, onSuccess, proposalId, initialTab = 'form', readOnly = false }) {
    const { user, hasPermission } = useAuth();
    const hasAccess = hasPermission('proposals', 'create') || hasPermission('proposals', 'read');
    const previewRef = useRef(null);

    const [clients, setClients] = useState([]);
    const [deals, setDeals] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [saving, setSaving] = useState(false);
    const [isLoadingProposal, setIsLoadingProposal] = useState(false);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [previewZoom, setPreviewZoom] = useState(1);
    const [showFullPreview, setShowFullPreview] = useState(false);

    useEffect(() => {
        if (isOpen) setActiveTab(initialTab);
    }, [isOpen, initialTab]);

    // Signature states
    const [authorizedSigFile, setAuthorizedSigFile] = useState(null);
    const [clientSigFile, setClientSigFile] = useState(null);
    const [authorizedSigPreview, setAuthorizedSigPreview] = useState(null);
    const [clientSigPreview, setClientSigPreview] = useState(null);

    const handleSigChange = (type, file) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        if (type === 'authorized') {
            setAuthorizedSigFile(file);
            setAuthorizedSigPreview(url);
        } else {
            setClientSigFile(file);
            setClientSigPreview(url);
        }
    };

    const removeSig = (type) => {
        if (type === 'authorized') {
            setAuthorizedSigFile(null);
            if (authorizedSigPreview) URL.revokeObjectURL(authorizedSigPreview);
            setAuthorizedSigPreview(null);
        } else {
            setClientSigFile(null);
            if (clientSigPreview) URL.revokeObjectURL(clientSigPreview);
            setClientSigPreview(null);
        }
    };

    const zoomIn = () => setPreviewZoom(prev => Math.min(prev + 0.15, 2));
    const zoomOut = () => setPreviewZoom(prev => Math.max(prev - 0.15, 0.5));
    const zoomReset = () => setPreviewZoom(1);

    const [form, setForm] = useState({
        title: '',
        date: new Date().toISOString().split('T')[0],
        validTill: '',
        clientId: '',
        clientName: '',
        clientEmail: '',
        clientPhone: '',
        clientAddress: '',
        contactName: '',
        deal: '',
        projectSections: [{ type: 'page-heading', content: '', page: 1 }],
        projectTimeline: '',
        items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
        subtotal: 0,
        taxPercentage: 18,
        taxAmount: 0,
        discount: 0,
        total: 0,
        paymentTerms: 'Payment Method: 50% Payment Advance • 30% in between • 20% After Completion',
        termsAndConditions: `1. Payment terms: 50% advance, 50% on project completion\n2. Project timeline is estimated and may vary based on client inputs\n3. Any additional features beyond scope will be quoted separately\n4. Client must provide all necessary access and resources\n5. All intellectual property rights remain with the company until full payment`,
        notes: '',
        template: '',
    });
    const [currentProjectPage, setCurrentProjectPage] = useState(1);

    // Selection mode states
    const [clientMode, setClientMode] = useState('select'); // 'select' or 'manual'

    const fetchData = useCallback(async () => {
        try {
            const [cRes, dRes, tRes] = await Promise.all([
                api.get('/clients'),
                api.get('/deals'),
                api.get('/proposal-templates'),
            ]);
            setClients(cRes.data);
            setDeals(dRes.data);
            setTemplates(tRes.data);
        } catch (err) {
            console.error('Error fetching data:', err);
        }
    }, []);

    const fetchProposal = useCallback(async () => {
        if (!proposalId) return;
        try {
            const res = await api.get(`/proposals/${proposalId}`);
            const p = res.data;
            setForm({
                title: p.title || '',
                date: p.date ? new Date(p.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                validTill: p.validTill ? new Date(p.validTill).toISOString().split('T')[0] : '',
                clientId: p.client?._id || '',
                clientName: p.clientName || '',
                clientEmail: p.clientEmail || '',
                clientPhone: p.clientPhone || '',
                clientAddress: p.clientAddress || '',
                contactName: p.contactName || '',
                deal: p.deal?._id || p.deal || '',
                projectSections: p.projectSections && p.projectSections.length > 0 ? p.projectSections : [{ type: 'page-heading', content: '', page: 1 }],
                projectTimeline: p.projectTimeline || '',
                items: p.items && p.items.length > 0 ? p.items : [{ description: '', quantity: 1, rate: 0, amount: 0 }],
                subtotal: p.subtotal || 0,
                taxPercentage: p.taxPercentage || 18,
                taxAmount: p.taxAmount || 0,
                discount: p.discount || 0,
                total: p.total || 0,
                paymentTerms: p.paymentTerms || 'Payment Method: 50% Payment Advance • 30% in between • 20% After Completion',
                termsAndConditions: p.termsAndConditions || `1. Payment terms: 50% advance, 50% on project completion\n2. Project timeline is estimated and may vary based on client inputs\n3. Any additional features beyond scope will be quoted separately\n4. Client must provide all necessary access and resources\n5. All intellectual property rights remain with the company until full payment`,
                notes: p.notes || '',
                template: p.template?._id || p.template || '',
            });
            setClientMode(p.client ? 'select' : 'manual');
            // Don't set loading to false here - let the useEffect handle it after render
        } catch (err) {
            console.error('Error fetching proposal:', err);
            alert('Error loading proposal data');
            setIsLoadingProposal(false);
        }
    }, [proposalId]);

    useEffect(() => { 
        if (hasAccess && isOpen) {
            // Start loading when modal opens with minimum 5 seconds
            setIsLoadingProposal(true);
            
            fetchData();
            if (proposalId) {
                fetchProposal().then(() => {
                    // Minimum 1 seconds loading to ensure proper rendering
                    setTimeout(() => {
                        setIsLoadingProposal(false);
                    }, 1000);
                });
            } else {
                setForm({
                    title: '',
                    date: new Date().toISOString().split('T')[0],
                    validTill: '',
                    clientId: '',
                    clientName: '',
                    clientEmail: '',
                    clientPhone: '',
                    clientAddress: '',
                    contactName: '',
                    deal: '',
                    projectSections: [{ type: 'page-heading', content: '', page: 1 }],
                    projectTimeline: '',
                    items: [{ description: '', quantity: 1, rate: 0, amount: 0 }],
                    subtotal: 0,
                    taxPercentage: 18,
                    taxAmount: 0,
                    discount: 0,
                    total: 0,
                    paymentTerms: 'Payment Method: 50% Payment Advance • 30% in between • 20% After Completion',
                    termsAndConditions: `1. Payment terms: 50% advance, 50% on project completion\n2. Project timeline is estimated and may vary based on client inputs\n3. Any additional features beyond scope will be quoted separately\n4. Client must provide all necessary access and resources\n5. All intellectual property rights remain with the company until full payment`,
                    notes: '',
                    template: '',
                });
                setClientMode('select');
                setAuthorizedSigFile(null);
                setClientSigFile(null);
                setAuthorizedSigPreview(null);
                setClientSigPreview(null);
                
                // For new proposals, minimum 5 seconds loading
                setTimeout(() => {
                    setIsLoadingProposal(false);
                }, 5000);
            }
        } else if (!isOpen) {
            // Reset loading state when modal closes
            setIsLoadingProposal(false);
        }
    }, [fetchData, fetchProposal, hasAccess, isOpen, proposalId]);

    // Auto calculate
    useEffect(() => {
        const subtotal = form.items.reduce((s, i) => s + (i.amount || 0), 0);
        const taxAmount = Math.round(subtotal * form.taxPercentage / 100 * 100) / 100;
        const total = subtotal + taxAmount - (form.discount || 0);
        setForm(p => ({ ...p, subtotal, taxAmount, total }));
    }, [form.items, form.taxPercentage, form.discount]);

    const set = (name, value) => setForm(p => ({ ...p, [name]: value }));
    const handleChange = (e) => set(e.target.name, e.target.value);

    const handleItemChange = (idx, field, val) => {
        const items = [...form.items];
        items[idx][field] = val;
        if (field === 'quantity' || field === 'rate') {
            items[idx].amount = items[idx].quantity * items[idx].rate;
        }
        set('items', items);
    };
    const addItem = () => set('items', [...form.items, { description: '', quantity: 1, rate: 0, amount: 0 }]);
    const removeItem = (idx) => { if (form.items.length > 1) set('items', form.items.filter((_, i) => i !== idx)); };

    // Project Sections handlers
    const addSection = (type = 'paragraph') => {
        set('projectSections', [...form.projectSections, { type, content: '', page: currentProjectPage }]);
    };
    const removeSection = (idx) => { if (form.projectSections.length > 1) set('projectSections', form.projectSections.filter((_, i) => i !== idx)); };
    const updateSection = (idx, field, val) => {
        const sections = [...form.projectSections];
        sections[idx] = { ...sections[idx], [field]: val };
        set('projectSections', sections);
    };
    const moveSection = (idx, dir) => {
        const sections = [...form.projectSections];
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= sections.length) return;
        [sections[idx], sections[newIdx]] = [sections[newIdx], sections[idx]];
        set('projectSections', sections);
    };

    const moveToNextProjectPage = () => {
        setCurrentProjectPage(prev => prev + 1);
    };

    const handleClientSelect = (id) => {
        const c = clients.find(x => x._id === id);
        if (c) setForm(p => ({
            ...p,
            clientId: c._id,
            clientName: c.company || c.name || '',
            contactName: c.name || '',
            clientEmail: c.email || '',
            clientPhone: c.phone || '',
            clientAddress: c.address || ''
        }));
        else setForm(p => ({ ...p, clientId: '', clientName: '', contactName: '', clientEmail: '', clientPhone: '', clientAddress: '' }));
    };

    const clearClientFields = () => {
        setForm(p => ({
            ...p,
            clientId: '',
            clientName: '',
            contactName: '',
            clientEmail: '',
            clientPhone: '',
            clientAddress: ''
        }));
    };

    const waitForPaint = () => new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 60)));

    const saveProposalWithSignatures = async () => {
        const cleanForm = { ...form };
        if (!cleanForm.clientName) {
            cleanForm.clientName = cleanForm.contactName || 'Client';
        }
        if (!cleanForm.deal || cleanForm.deal === '') delete cleanForm.deal;
        if (!cleanForm.template || cleanForm.template === '') delete cleanForm.template;

        let res;
        if (proposalId) {
            res = await api.put(`/proposals/${proposalId}`, cleanForm);
        } else {
            res = await api.post('/proposals', cleanForm);
        }

        if (authorizedSigFile || clientSigFile) {
            const fd = new FormData();
            if (authorizedSigFile) fd.append('authorizedSignature', authorizedSigFile);
            if (clientSigFile) fd.append('clientSignature', clientSigFile);
            await api.post(`/proposals/${res.data._id}/signatures`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        }

        return res.data;
    };

    const generatePDF = async (fileName) => {
        const prevTab = activeTab;
        const prevZoom = previewZoom;
        let zoomArea = null;
        let originalZoomAreaTransform = '';
        let originalZoomAreaTransformOrigin = '';

        const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

        const waitForFrames = async (count = 2) => {
            for (let i = 0; i < count; i++) {
                await nextFrame();
            }
        };

        const ensureElementVisible = (el, label) => {
            if (!el) throw new Error(`${label} not found`);
            const style = window.getComputedStyle(el);
            const rect = el.getBoundingClientRect();
            const isHidden = style.display === 'none' || style.visibility === 'hidden';
            const hasSize = rect.width > 0 && rect.height > 0;
            console.log(`[PDF] ${label} size:`, { width: rect.width, height: rect.height, display: style.display, visibility: style.visibility });
            if (isHidden || !hasSize) {
                throw new Error(`${label} is hidden or has zero size`);
            }
        };

        const waitForInlineImages = async (root) => {
            const imgs = Array.from(root.querySelectorAll('img'));
            await Promise.all(imgs.map((img) => {
                if (!img.getAttribute('crossorigin')) {
                    img.setAttribute('crossorigin', 'anonymous');
                }

                if (img.complete) {
                    return Promise.resolve();
                }

                return new Promise((resolve) => {
                    const onDone = () => resolve();
                    img.addEventListener('load', onDone, { once: true });
                    img.addEventListener('error', onDone, { once: true });
                });
            }));
        };

        const renderSafePage = async (pageEl, pageKey, stripAllBackgrounds = false) => {
            const rect = pageEl.getBoundingClientRect();
            const exportWidth = Math.max(1, Math.round(rect.width));
            const exportHeight = Math.max(1, Math.round(rect.height));

            const canvas = await html2canvas(pageEl, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                backgroundColor: '#ffffff',
                logging: false,
                imageTimeout: 0,
                width: exportWidth,
                height: exportHeight,
                onclone: (clonedDoc) => {
                    const targetPage = clonedDoc.querySelector(`[data-pdf-page-key="${pageKey}"]`);
                    if (targetPage) {
                        const whiteBaseStyle = clonedDoc.createElement('style');
                        whiteBaseStyle.textContent = `
                            [data-pdf-page-key="${pageKey}"] {
                                background: #ffffff !important;
                            }
                            [data-pdf-page-key="${pageKey}"] .pg-page-border {
                                background: #ffffff !important;
                                box-shadow: none !important;
                            }
                        `;
                        clonedDoc.head.appendChild(whiteBaseStyle);
                    }

                    if (stripAllBackgrounds) {
                        const target = clonedDoc.querySelector(`[data-pdf-page-key="${pageKey}"]`);
                        if (!target) return;

                        const styleTag = clonedDoc.createElement('style');
                        styleTag.textContent = `
                            [data-pdf-page-key="${pageKey}"],
                            [data-pdf-page-key="${pageKey}"] * {
                                background-image: none !important;
                                -webkit-mask-image: none !important;
                                mask-image: none !important;
                                border-image: none !important;
                                list-style-image: none !important;
                            }
                            [data-pdf-page-key="${pageKey}"] *::before,
                            [data-pdf-page-key="${pageKey}"] *::after {
                                background-image: none !important;
                                -webkit-mask-image: none !important;
                                mask-image: none !important;
                                border-image: none !important;
                                list-style-image: none !important;
                            }
                        `;
                        clonedDoc.head.appendChild(styleTag);
                    }
                }
            });

            console.log('[PDF] Canvas dimensions:', {
                width: canvas?.width || 0,
                height: canvas?.height || 0,
                exportWidth,
                exportHeight,
                pageKey
            });

            if (!canvas || canvas.width === 0 || canvas.height === 0) {
                throw new Error(`Canvas for page ${pageKey} is empty`);
            }

            return canvas;
        };

        const originalPageKeys = [];

        try {
            if (prevTab !== 'preview') setActiveTab('preview');
            if (prevZoom !== 1) setPreviewZoom(1);

            await waitForFrames(3);
            await waitForPaint();

            const previewRoot = previewRef.current;
            if (!previewRoot) throw new Error('Preview root not found');

            // Neutralize zoom transform at DOM level so html2canvas captures true layout.
            zoomArea = previewRoot.querySelector('.pg-preview-zoom-area');
            if (zoomArea) {
                originalZoomAreaTransform = zoomArea.style.transform;
                originalZoomAreaTransformOrigin = zoomArea.style.transformOrigin;
                zoomArea.style.transform = 'none';
                zoomArea.style.transformOrigin = 'top left';
                await waitForFrames(2);
            }

            const pages = Array.from(previewRoot.querySelectorAll('.pg-page'));
            if (!pages.length) throw new Error('Preview pages not found');

            const previewPanel = previewRoot.closest('.pg-preview-panel') || previewRoot.parentElement;
            ensureElementVisible(previewPanel, 'Preview panel');
            pages.forEach((page, idx) => ensureElementVisible(page, `Page ${idx + 1}`));

            pages.forEach((page, idx) => {
                const oldVal = page.getAttribute('data-pdf-page-key');
                originalPageKeys.push(oldVal);
                page.setAttribute('data-pdf-page-key', String(idx));
            });

            await waitForInlineImages(previewRoot);
            await waitForFrames(2);

            const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
            const pdfW = 210;
            const pdfH = 297;

            for (let i = 0; i < pages.length; i++) {
                const page = pages[i];
                const rect = page.getBoundingClientRect();
                if (rect.width === 0 || rect.height === 0) {
                    throw new Error(`Page ${i + 1} has zero width or height before render`);
                }

                let canvas;
                try {
                    canvas = await renderSafePage(page, String(i), false);
                } catch (error) {
                    const message = error?.message || String(error);
                    if (!/createPattern|InvalidStateError/i.test(message)) {
                        throw error;
                    }
                    console.warn(`[PDF] Retrying page ${i + 1} with background sanitization due to pattern error`);
                    canvas = await renderSafePage(page, String(i), true);
                }

                const imgData = canvas.toDataURL('image/jpeg', 0.96);
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, pdfW, pdfH, undefined, 'FAST');
            }

            pdf.save(fileName);
        } catch (error) {
            console.error('PDF generation error:', error);
            alert('Error generating PDF: ' + (error?.message || 'Unknown error'));
        } finally {
            if (zoomArea) {
                zoomArea.style.transform = originalZoomAreaTransform;
                zoomArea.style.transformOrigin = originalZoomAreaTransformOrigin;
            }

            const previewRoot = previewRef.current;
            if (previewRoot) {
                const pages = Array.from(previewRoot.querySelectorAll('.pg-page'));
                pages.forEach((page, idx) => {
                    const oldVal = originalPageKeys[idx];
                    if (oldVal === null || oldVal === undefined) {
                        page.removeAttribute('data-pdf-page-key');
                    } else {
                        page.setAttribute('data-pdf-page-key', oldVal);
                    }
                });
            }

            setActiveTab(prevTab);
            setPreviewZoom(prevZoom);
        }
    };

    const downloadExactPreviewPDF = async (fileName) => {
        const prevTab = activeTab;
        const prevZoom = previewZoom;

        const waitFrames = async (count = 2) => {
            for (let i = 0; i < count; i++) {
                await new Promise(resolve => requestAnimationFrame(resolve));
            }
        };

        try {
            if (prevTab !== 'preview') setActiveTab('preview');
            if (prevZoom !== 1) setPreviewZoom(1);

            await waitFrames(3);
            await waitForPaint();

            const previewRoot = previewRef.current;
            if (!previewRoot) throw new Error('Preview root not available');

            const previewWrap = previewRoot.querySelector('.pg-preview-wrap');
            if (!previewWrap) throw new Error('Preview content not available');

            const printWindow = window.open('', '_blank', 'width=1200,height=900');
            if (!printWindow) {
                throw new Error('Popup blocked. Please allow popups for this site.');
            }

            const styleNodes = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'));
            const stylesHtml = styleNodes.map(node => node.outerHTML).join('\n');

            const printHtml = `
                <!doctype html>
                <html>
                <head>
                    <meta charset="utf-8" />
                    <meta name="viewport" content="width=device-width, initial-scale=1" />
                    <title>${fileName || 'proposal'}</title>
                    ${stylesHtml}
                    <style>
                        @page { size: A4; margin: 0; }
                        html, body {
                            margin: 0;
                            padding: 0;
                            background: #fff;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .print-root {
                            margin: 0;
                            padding: 0;
                        }
                        .print-root .pg-preview-wrap {
                            gap: 0 !important;
                        }
                        .print-root .pg-page {
                            width: 210mm !important;
                            height: 297mm !important;
                            aspect-ratio: auto !important;
                            box-shadow: none !important;
                            border-radius: 0 !important;
                            margin: 0 auto !important;
                            break-after: page;
                            page-break-after: always;
                        }
                        .print-root .pg-page:last-child {
                            break-after: auto;
                            page-break-after: auto;
                        }
                    </style>
                </head>
                <body>
                    <div class="print-root">${previewWrap.outerHTML}</div>
                    <script>
                        (function () {
                            const triggerPrint = function () {
                                requestAnimationFrame(function () {
                                    requestAnimationFrame(function () {
                                        window.focus();
                                        window.print();
                                    });
                                });
                            };

                            if (document.readyState === 'complete') {
                                triggerPrint();
                            } else {
                                window.addEventListener('load', triggerPrint, { once: true });
                            }

                            window.addEventListener('afterprint', function () {
                                window.close();
                            });
                        })();
                    </script>
                </body>
                </html>
            `;

            printWindow.document.open();
            printWindow.document.write(printHtml);
            printWindow.document.close();
        } catch (error) {
            console.error('Exact preview PDF error:', error);
            throw error;
        } finally {
            setActiveTab(prevTab);
            setPreviewZoom(prevZoom);
        }
    };

    const downloadFromPreview = async (fileName) => {
        await generatePDF(fileName);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const savedProposal = await saveProposalWithSignatures();

            alert(`Proposal ${proposalId ? 'updated' : 'created'} successfully!`);
            if (onSuccess) onSuccess(savedProposal);
            if (onClose) onClose();
        } catch (err) {
            alert('Error: ' + (err.response?.data?.message || err.message));
        }
        setSaving(false);
    };

    const downloadPreview = async () => {
        if (readOnly) {
            await downloadFromPreview(`proposal-${form.proposalNumber || 'view'}.pdf`);
            return;
        }
        // Save first
        setSaving(true);
        try {
            const savedProposal = await saveProposalWithSignatures();
            if (onSuccess) onSuccess(savedProposal);
            if (onClose) onClose();
        } catch (err) {
            alert('Error saving proposal: ' + (err.response?.data?.message || err.message));
        }
        setSaving(false);
    };

    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    const fmtINR = (n) => Number(n || 0).toLocaleString('en-IN');

    const renderRepeatedHeader = () => (
        <>
            <div className="pg-header">
                <div className="pg-header-logo">
                    <img
                        src="/logo.png"
                        alt="Logo"
                        className="pg-logo-img"
                        onError={e => {
                            e.target.onerror = null;
                            e.target.src = '';
                            e.target.style.display = 'none';
                        }}
                        onLoad={e => {
                            if (e.target.naturalWidth === 0 || e.target.naturalHeight === 0) {
                                e.target.style.display = 'none';
                            }
                        }}
                        width={100}
                        height={35}
                    />
                </div>
                <div className="pg-header-info">
                    <div className="pg-header-company">{COMPANY.name}</div>
                    <div className="pg-header-detail">{COMPANY.website} | {COMPANY.email}</div>
                    <div className="pg-header-detail">GSTIN: {COMPANY.gstin} | CIN: {COMPANY.cin}</div>
                    <div className="pg-header-detail">Registered Office: {COMPANY.address}</div>
                </div>
            </div>
            <div className="pg-header-line" />
        </>
    );

    const renderRepeatedFooter = () => (
        <div className="pg-footer">
            <div className="pg-footer-line" />
            <div className="pg-footer-row">
                <span>{COMPANY.shortName}</span>
                <span>{COMPANY.address}</span>
                <span className="pg-footer-link">{COMPANY.email}</span>
                <span className="pg-footer-link">{COMPANY.website}</span>
            </div>
        </div>
    );

    if (!hasAccess) {
        return (
            <div className="pg-container">
                <div className="pg-access-denied">
                    <div className="pg-ad-icon">🔒</div>
                    <h3>Access Denied</h3>
                    <p>You don't have permission to access the Proposal Generator.</p>
                </div>
            </div>
        );
    }

    // ======================== LIVE PREVIEW ========================
    // Always keep previewRef attached to a hidden root in the DOM
    const renderPreview = () => (

        <div className="pg-preview-wrap">
            {/* ========== PAGE 1: Company Profile ========== */}
            <div className="pg-page">
                <div className="pg-page-border">
                    {/* Header */}
                    <div className="pg-header">
                        <div className="pg-header-logo">
                            <img
                                src="/logo.png"
                                alt="Logo"
                                className="pg-logo-img"
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                }}
                                onLoad={e => {
                                    if (e.target.naturalWidth === 0 || e.target.naturalHeight === 0) {
                                        e.target.style.display = 'none';
                                    }
                                }}
                                width={100}
                                height={35}
                            />
                        </div>
                        <div className="pg-header-info pg-header-info--first-page">
                            <div className="pg-header-company pg-header-company--first-page">{COMPANY.name}</div>
                            <div className="pg-header-detail pg-header-detail--first-page">{COMPANY.website} | {COMPANY.email}</div>
                            <div className="pg-header-detail pg-header-detail--first-page">GSTIN: {COMPANY.gstin} | CIN: {COMPANY.cin}</div>
                            <div className="pg-header-detail pg-header-detail--first-page">Registered Office: {COMPANY.address}</div>
                        </div>
                    </div>
                    <div className="pg-header-line" />

                    {/* Title */}
                    <div className="pg-title">PROJECT PROPOSAL</div>
                    <div className="pg-subtitle">{form.title || 'Dynamic Website'}</div>

                    {/* Company Info Table */}
                    <table className="pg-info-table">
                        <tbody>
                            <tr><td className="pg-it-label">Prepared By</td><td className="pg-it-value">{COMPANY.shortName}</td></tr>
                            <tr><td className="pg-it-label">Website</td><td className="pg-it-value">{COMPANY.website}</td></tr>
                            <tr><td className="pg-it-label">Email</td><td className="pg-it-value">{COMPANY.email}</td></tr>
                            <tr><td className="pg-it-label">Address</td><td className="pg-it-value">{COMPANY.address}</td></tr>
                        </tbody>
                    </table>

                    {/* Client Info */}
                    <div className="pg-section-title">CLIENT INFORMATION</div>
                    <table className="pg-info-table pg-client-table">
                        <tbody>
                            {/* Client Name row removed as per request */}
                            <tr><td className="pg-it-label">Contact Person</td><td className="pg-it-value">{form.contactName || '—'}</td></tr>
                            <tr><td className="pg-it-label">Email</td><td className="pg-it-value">{form.clientEmail || '—'}</td></tr>
                            <tr><td className="pg-it-label">Phone</td><td className="pg-it-value">{form.clientPhone || '—'}</td></tr>
                            <tr><td className="pg-it-label">Address</td><td className="pg-it-value">{form.clientAddress || '—'}</td></tr>
                        </tbody>
                    </table>

                    {form.proposalNumber && (
                        <div className="pg-meta-row">
                            <span>Proposal #: {form.proposalNumber}</span>
                        </div>
                    )}

                    {/* Profile */}
                    <div className="pg-section-title">SUMMARY OF COMPANY PROFILE</div>
                    <p className="pg-body-text">{PROFILE_TEXT}</p>

                    {/* Our Services (moved to first page) */}
                    <div className="pg-section-title">OUR SERVICES</div>
                    <div className="pg-services-grid">
                        {SERVICES.map((s, i) => (
                            <div key={i} className="pg-service-cell">• {s}</div>
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="pg-footer">
                        <div className="pg-footer-line" />
                        <div className="pg-footer-row">
                            <span>{COMPANY.shortName}</span>
                            <span>{COMPANY.address}</span>
                            <span className="pg-footer-link">{COMPANY.email}</span>
                            <span className="pg-footer-link">{COMPANY.website}</span>
                        </div>
                    </div>
                </div>
            </div>

            <PaginatedProjectPreview
                sections={form.projectSections}
                timeline={form.projectTimeline}
                renderHeader={renderRepeatedHeader}
                renderFooter={renderRepeatedFooter}
            />

            {/* ========== PAGE 3: Costing & Payment ========== */}
            <div className="pg-page">
                <div className="pg-page-border">
                    {/* Header */}
                    <div className="pg-header">
                        <div className="pg-header-logo">
                            <img
                                src="/logo.png"
                                alt="Logo"
                                className="pg-logo-img"
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.src = '';
                                    e.target.style.display = 'none';
                                }}
                                onLoad={e => {
                                    if (e.target.naturalWidth === 0 || e.target.naturalHeight === 0) {
                                        e.target.style.display = 'none';
                                    }
                                }}
                                width={100}
                                height={35}
                            />
                        </div>
                        <div className="pg-header-info">
                            <div className="pg-header-company">{COMPANY.name}</div>
                            <div className="pg-header-detail">{COMPANY.website} | {COMPANY.email}</div>
                            <div className="pg-header-detail">GSTIN: {COMPANY.gstin} | CIN: {COMPANY.cin}</div>
                            <div className="pg-header-detail">Registered Office: {COMPANY.address}</div>
                        </div>
                    </div>
                    <div className="pg-header-line" />
                    <div className="pg-costing-header">
                        COSTING FOR {(form.title || 'PROJECT').toUpperCase()}
                    </div>
                    <table className="pg-cost-table">
                        <thead>
                            <tr>
                                <th>Individuals</th>
                                <th>Term's</th>
                                <th>Price Rs.(₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {form.items.map((item, idx) => (
                                <tr key={idx}>
                                    <td>{item.description || '—'}</td>
                                    <td>{item.quantity > 1 ? `${item.quantity} units` : 'Total'}</td>
                                    <td className="pg-ct-amount">{fmtINR(item.amount)}/-</td>
                                </tr>
                            ))}
                            <tr className="pg-ct-summary">
                                <td colSpan="2"><strong>Sub Total</strong></td>
                                <td className="pg-ct-amount"><strong>{fmtINR(form.subtotal)}/-</strong></td>
                            </tr>
                            {form.taxAmount > 0 && (
                                <tr className="pg-ct-summary">
                                    <td colSpan="2">GST {form.taxPercentage}%</td>
                                    <td className="pg-ct-amount">{fmtINR(form.taxAmount)}/-</td>
                                </tr>
                            )}
                            {form.discount > 0 && (
                                <tr className="pg-ct-summary">
                                    <td colSpan="2">Discount</td>
                                    <td className="pg-ct-amount">-{fmtINR(form.discount)}/-</td>
                                </tr>
                            )}
                            <tr className="pg-ct-total">
                                <td colSpan="2"><strong>Offered Price / Net Amount</strong></td>
                                <td className="pg-ct-amount"><strong>{fmtINR(form.total)}/-</strong></td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Payment Details */}
                    <div className="pg-costing-header">
                        PAYMENT DETAILS
                    </div>
                    <table className="pg-info-table pg-bank-table">
                        <tbody>
                            <tr><td className="pg-it-label">Account Name</td><td className="pg-it-value">{COMPANY.accountName}</td></tr>
                            <tr><td className="pg-it-label">Account Number</td><td className="pg-it-value">{COMPANY.accountNumber}</td></tr>
                            <tr><td className="pg-it-label">IFSC Code</td><td className="pg-it-value">{COMPANY.ifsc}</td></tr>
                            <tr><td className="pg-it-label">Bank Name</td><td className="pg-it-value">{COMPANY.bankName}</td></tr>
                            <tr><td className="pg-it-label">UPI ID</td><td className="pg-it-value">{COMPANY.upi}</td></tr>
                        </tbody>
                    </table>

                    <div className="pg-payment-note">
                        Note: Pay only on Company's account or on QR Code. Don't Pay on any employee's account or QR Code.
                    </div>

                    <div className="pg-tc-title">TERMS AND CONDITIONS</div>

                    <div className="pg-tc-content">
                        {form.termsAndConditions && form.termsAndConditions.split('\n').map((line, i) => (
                            <p key={i} className="pg-tc-line">{line}</p>
                        ))}
                    </div>

                    <p className="pg-tc-bold">If you cancel the project midway, the company will not refund your payment.</p>
                    <p className="pg-tc-refund">
                        <strong>Refund Policy:</strong> If the Project is canceled by the Company (BTPL), your money will be refunded within 45 days.
                    </p>

                    <div className="pg-meta-row" style={{ marginTop: '12px' }}>
                        <span>Date: {fmtDate(form.date)}</span>
                    </div>

                    {form.notes && (
                        <>
                            <div className="pg-section-title" style={{ marginTop: '16px' }}>ADDITIONAL NOTES</div>
                            <p className="pg-body-text">{form.notes}</p>
                        </>
                    )}

                    {/* Footer */}
                    <div className="pg-footer">
                        <div className="pg-footer-line" />
                        <div className="pg-footer-row">
                            <span>{COMPANY.shortName}</span>
                            <span>{COMPANY.address}</span>
                            <span className="pg-footer-link">{COMPANY.email}</span>
                            <span className="pg-footer-link">{COMPANY.website}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pg-page">
                <div className="pg-page-border">
                    <div className="pg-header">
                        <div className="pg-header-logo">
                            <img
                                src="/logo.png"
                                alt="Logo"
                                className="pg-logo-img"
                                onError={e => {
                                    e.target.onerror = null;
                                    e.target.src = '';
                                    e.target.style.display = 'none';
                                }}
                                onLoad={e => {
                                    if (e.target.naturalWidth === 0 || e.target.naturalHeight === 0) {
                                        e.target.style.display = 'none';
                                    }
                                }}
                                width={100}
                                height={35}
                            />
                        </div>
                        <div className="pg-header-info">
                            <div className="pg-header-company">{COMPANY.name}</div>
                            <div className="pg-header-detail">{COMPANY.website} | {COMPANY.email}</div>
                            <div className="pg-header-detail">GSTIN: {COMPANY.gstin} | CIN: {COMPANY.cin}</div>
                            <div className="pg-header-detail">Registered Office: {COMPANY.address}</div>
                        </div>
                    </div>
                    <div className="pg-header-line" />

                    <div className="pg-section-title">APPROVAL AND SIGNATURES</div>

                    <div className="pg-regards">
                        <p>Regards,</p>
                        <p><strong>{COMPANY.shortName}</strong></p>
                        <p>Add – {COMPANY.address}</p>
                        <p>Email: {COMPANY.email}</p>
                    </div>

                    <div className="pg-signatures">
                        <div className="pg-sig-block">
                            <div className="pg-sig-for">For {COMPANY.shortName}</div>
                            {authorizedSigPreview ? (
                                <img src={authorizedSigPreview} alt="Authorized Signature" style={{ maxHeight: '50px', maxWidth: '150px', objectFit: 'contain', margin: '8px 0' }} />
                            ) : (
                                <div className="pg-sig-line" />
                            )}
                            <div className="pg-sig-label"><strong>Authorized Signatory</strong></div>
                            <div className="pg-sig-name">{COMPANY.shortName}</div>
                        </div>
                        <div className="pg-sig-block">
                            <div className="pg-sig-for">Acceptance / Client Signature</div>
                            {clientSigPreview ? (
                                <img src={clientSigPreview} alt="Client Signature" style={{ maxHeight: '50px', maxWidth: '150px', objectFit: 'contain', margin: '8px 0' }} />
                            ) : (
                                <div className="pg-sig-line" />
                            )}
                            <div className="pg-sig-label"><strong>Client Name & Signature</strong></div>
                            <div className="pg-sig-name">Date: ________________</div>
                        </div>
                    </div>

                    <div className="pg-footer">
                        <div className="pg-footer-line" />
                        <div className="pg-footer-row">
                            <span>{COMPANY.shortName}</span>
                            <span>{COMPANY.address}</span>
                            <span className="pg-footer-link">{COMPANY.email}</span>
                            <span className="pg-footer-link">{COMPANY.website}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    if (!isOpen) return null;

    return (
        <div className="pg-modal-overlay">
            {/* Loading Spinner Overlay */}
            {isLoadingProposal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10001,
                    backdropFilter: 'blur(4px)'
                }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        border: '4px solid rgba(255, 255, 255, 0.3)',
                        borderTop: '4px solid #ffffff',
                        borderRadius: '50%',
                        animation: 'domestic-spinner 1s linear infinite'
                    }} />
                    <div style={{
                        color: '#ffffff',
                        fontSize: '16px',
                        fontWeight: 600,
                        marginTop: '20px',
                        fontFamily: "'Inter', -apple-system, sans-serif"
                    }}>
                        {readOnly ? 'Loading Proposal...' : 'Loading Proposal for Editing...'}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes domestic-spinner {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>

            <div className="pg-modal-content">
                <button className="pg-modal-close" onClick={onClose} title="Close Generator">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
                <div className="pg-container">
                    {/* Top bar */}
                    <div className="pg-topbar">
                        <div style={{ padding: '0 24px' }}>
                            <h1 className="pg-page-title">{proposalId ? (readOnly ? 'View Proposal' : 'Edit Proposal') : 'Proposal Generator'}</h1>
                            <p className="pg-page-subtitle">{readOnly ? 'Detailed preview of the client proposal' : 'Create professional proposals with live preview'}</p>
                        </div>
                        <div className="pg-topbar-actions" style={{ padding: '0 24px' }}>
                        </div>
                    </div>

                    {/* Mobile tab toggle */}
                    {!readOnly && (
                        <div className="pg-tab-bar">
                            <button className={`pg-tab ${activeTab === 'form' ? 'pg-tab-active' : ''}`} onClick={() => setActiveTab('form')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                                Form
                            </button>
                            <button className={`pg-tab ${activeTab === 'preview' ? 'pg-tab-active' : ''}`} onClick={() => setActiveTab('preview')}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                Preview
                            </button>
                        </div>
                    )}

                    <div className="pg-layout" style={{ 
                        height: 'calc(100% - 100px)', 
                        overflow: 'hidden',
                        gridTemplateColumns: readOnly ? '1fr' : undefined 
                    }}>
                        {/* LEFT: Form */}
                        {!readOnly && (
                            <div className={`pg-form-panel ${activeTab === 'form' ? 'pg-panel-visible' : ''}`} style={{ height: '100%', overflowY: 'auto', padding: '0 24px 40px' }}>
                                <div className="pg-form-header" style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <h2 className="pg-form-title" style={{ fontSize: '18px', fontWeight: 700, color: '#111827', margin: 0 }}>{proposalId ? 'Update' : 'Generate'} Proposal</h2>
                                        <p className="pg-form-subtitle" style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>Fill in the details below to {proposalId ? 'update' : 'create'} a professional proposal</p>
                                    </div>
                                </div>
                                <form onSubmit={handleSave}>
                                    <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                                    {/* Basic */}
                                    <div className="pg-form-section">
                                        <div className="pg-fs-header">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                            Basic Information
                                        </div>
                                        <div className="pg-grid-2">
                                            <div className="pg-field">
                                                <label className="pg-lbl">Proposal Title <span className="pg-req">*</span></label>
                                                <input type="text" name="title" className="pg-input" placeholder="e.g. Dynamic Website" value={form.title} onChange={handleChange} required />
                                            </div>
                                            {/* Template field removed as per request */}
                                        </div>
                                        <div className="pg-grid-2">
                                            <div className="pg-field">
                                                <label className="pg-lbl">Date <span className="pg-req">*</span></label>
                                                <input type="date" name="date" className="pg-input" value={form.date} onChange={handleChange} required />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Client */}
                                    <div className="pg-form-section">
                                        <div className="pg-fs-header">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                            Client Information
                                        </div>

                                        {/* Client Mode Toggle */}
                                        <div className="pg-mode-toggle" style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #e2e8f0', paddingBottom: '12px' }}>
                                            <button
                                                type="button"
                                                className={`pg-mode-btn ${clientMode === 'select' ? 'pg-mode-active' : ''}`}
                                                onClick={() => {
                                                    setClientMode('select');
                                                    clearClientFields();
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #cbd5e1',
                                                    background: clientMode === 'select' ? '#3b775e' : '#f1f5f9',
                                                    color: clientMode === 'select' ? '#fff' : '#475569',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    transition: 'all .2s'
                                                }}
                                            >
                                                📋 Select Existing
                                            </button>
                                            <button
                                                type="button"
                                                className={`pg-mode-btn ${clientMode === 'manual' ? 'pg-mode-active' : ''}`}
                                                onClick={() => {
                                                    setClientMode('manual');
                                                    clearClientFields();
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #cbd5e1',
                                                    background: clientMode === 'manual' ? '#3b775e' : '#f1f5f9',
                                                    color: clientMode === 'manual' ? '#fff' : '#475569',
                                                    cursor: 'pointer',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                    transition: 'all .2s'
                                                }}
                                            >
                                                ✏️ Enter Manually
                                            </button>
                                        </div>

                                        {/* Select Existing Client */}
                                        {clientMode === 'select' && (
                                            <div className="pg-grid-2">
                                                <div className="pg-field">
                                                    <label className="pg-lbl">Select Client <span className="pg-req">*</span></label>
                                                    <select className="pg-input pg-select" onChange={(e) => handleClientSelect(e.target.value)} value={form.clientId || ''}>
                                                        <option value="">-- Choose a client --</option>
                                                        {clients.map(c => {
                                                            const displayText = `${c.company || c.name} ${c.email ? `(${c.email})` : ''}`;
                                                            return <option key={c._id} value={c._id}>{displayText}</option>;
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        {/* Manual Client Entry intentionally starts with contact fields */}
                                        {clientMode === 'manual' && (
                                            <div className="pg-field">
                                                <label className="pg-lbl">Client/Company Name <span className="pg-req">*</span></label>
                                                <input type="text" name="clientName" className="pg-input" placeholder="e.g. Acme Corp" value={form.clientName} onChange={handleChange} required />
                                            </div>
                                        )}

                                        {/* Common Details (always visible after selection/entry) */}
                                        {(clientMode === 'manual' || form.clientName || form.clientId) && (
                                            <>
                                                <div className="pg-grid-2">
                                                    <div className="pg-field">
                                                        <label className="pg-lbl">Contact Person</label>
                                                        <input type="text" name="contactName" className="pg-input" placeholder="Contact Person" value={form.contactName} onChange={handleChange} />
                                                    </div>
                                                    <div className="pg-field">
                                                        <label className="pg-lbl">Email</label>
                                                        <input type="email" name="clientEmail" className="pg-input" placeholder="client@email.com" value={form.clientEmail} onChange={handleChange} />
                                                    </div>
                                                </div>
                                                <div className="pg-grid-2">
                                                    <div className="pg-field">
                                                        <label className="pg-lbl">Phone</label>
                                                        <input type="tel" name="clientPhone" className="pg-input" placeholder="+91 XXXXXXXXXX" value={form.clientPhone} onChange={handleChange} />
                                                    </div>
                                                    <div className="pg-field">
                                                        <label className="pg-lbl">Address</label>
                                                        <input type="text" name="clientAddress" className="pg-input" placeholder="Complete address" value={form.clientAddress} onChange={handleChange} />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Project */}
                                    <div className="pg-form-section">
                                        <div className="pg-fs-header" style={{ justifyContent: 'space-between' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
                                                Project Details
                                            </span>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 700, color: '#334155', background: '#e2e8f0', borderRadius: '6px', padding: '4px 8px' }}>
                                                    Adding to Page {currentProjectPage}
                                                </span>
                                                <button type="button" className="pg-btn-add" onClick={moveToNextProjectPage}>Next Page</button>
                                            </div>
                                        </div>

                                        <div className="pg-section-add-btns" style={{ marginBottom: '16px' }}>
                                            <button type="button" className="pg-btn-add" onClick={() => addSection('page-heading')}>+ Page Heading</button>
                                            <button type="button" className="pg-btn-add" onClick={() => addSection('heading')}>+ Heading</button>
                                            <button type="button" className="pg-btn-add" onClick={() => addSection('sub-heading')}>+ Sub Heading</button>
                                            <button type="button" className="pg-btn-add" onClick={() => addSection('bullet')}>+ Bullet</button>
                                            <button type="button" className="pg-btn-add" onClick={() => addSection('paragraph')}>+ Paragraph</button>
                                            <button type="button" className="pg-btn-add" onClick={() => addSection('formatted-paragraph')} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>+ Formatted Paragraph</button>
                                        </div>

                                        <div className="pg-section-list-scroll">
                                            {form.projectSections.map((sec, i) => (
                                                <div className={`pg-section-block pg-section-block--${sec.type}`} key={i}>
                                                    <div className="pg-section-block-header">
                                                        <select className="pg-input pg-section-type-select" value={sec.type} onChange={(e) => updateSection(i, 'type', e.target.value)}>
                                                            <option value="page-heading">Page Heading</option>
                                                            <option value="heading">Heading</option>
                                                            <option value="sub-heading">Sub Heading</option>
                                                            <option value="bullet">Bullet Point</option>
                                                            <option value="paragraph">Paragraph</option>
                                                            <option value="formatted-paragraph">Formatted Paragraph</option>
                                                        </select>
                                                        <div className="pg-section-block-actions">
                                                            <button type="button" className="pg-section-move-btn" onClick={() => moveSection(i, -1)} disabled={i === 0} title="Move up">↑</button>
                                                            <button type="button" className="pg-section-move-btn" onClick={() => moveSection(i, 1)} disabled={i === form.projectSections.length - 1} title="Move down">↓</button>
                                                            {form.projectSections.length > 1 && (
                                                                <button type="button" className="pg-btn-remove" onClick={() => removeSection(i)} title="Remove">×</button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {sec.type === 'paragraph' || sec.type === 'bullet' || sec.type === 'formatted-paragraph' ? (
                                                        <textarea 
                                                            className="pg-input pg-textarea" 
                                                            rows={sec.type === 'bullet' ? 2 : 3} 
                                                            placeholder={sec.type === 'bullet' ? 'One bullet per line...' : sec.type === 'formatted-paragraph' ? 'Paste formatted text here (spaces & line breaks will be preserved)...' : 'Write paragraph content...'} 
                                                            value={sec.content} 
                                                            onChange={(e) => updateSection(i, 'content', e.target.value)}
                                                            style={sec.type === 'formatted-paragraph' ? { fontFamily: 'monospace', whiteSpace: 'pre-wrap' } : {}}
                                                        />
                                                    ) : (
                                                        <input type="text" className="pg-input" placeholder={sec.type === 'page-heading' ? 'e.g. Admin Panel (Core System)' : sec.type === 'heading' ? 'e.g. 1. Dashboard' : 'e.g. Frontend Development'} value={sec.content} onChange={(e) => updateSection(i, 'content', e.target.value)} />
                                                    )}
                                                </div>
                                            ))}
                                        </div>

                                        <div className="pg-field" style={{ marginTop: '12px' }}>
                                            <label className="pg-lbl">Timeline</label>
                                            <input type="text" name="projectTimeline" className="pg-input" placeholder="e.g. 8–10 weeks" value={form.projectTimeline} onChange={handleChange} />
                                        </div>
                                    </div>

                                    {/* Pricing */}
                                    <div className="pg-form-section">
                                        <div className="pg-fs-header" style={{ justifyContent: 'space-between' }}>
                                            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                                                Services & Pricing
                                            </span>
                                            <button type="button" className="pg-btn-add" onClick={addItem}>+ Add</button>
                                        </div>

                                        <div className="pg-items-header">
                                            <span>Description</span><span>Qty</span><span>Rate (₹)</span><span>Amount</span><span></span>
                                        </div>
                                        {form.items.map((item, i) => (
                                            <div className="pg-item-row" key={i}>
                                                <input type="text" className="pg-input" placeholder="Service name" value={item.description} onChange={(e) => handleItemChange(i, 'description', e.target.value)} required />
                                                <input type="number" className="pg-input" min="1" value={item.quantity} onChange={(e) => handleItemChange(i, 'quantity', parseFloat(e.target.value) || 0)} />
                                                <input type="number" className="pg-input" min="0" value={item.rate} onChange={(e) => handleItemChange(i, 'rate', parseFloat(e.target.value) || 0)} />
                                                <input type="number" className="pg-input pg-readonly" value={item.amount} readOnly />
                                                {form.items.length > 1 ? (
                                                    <button type="button" className="pg-btn-remove" onClick={() => removeItem(i)}>×</button>
                                                ) : <span />}
                                            </div>
                                        ))}

                                        <div className="pg-totals">
                                            <div className="pg-total-row"><span>Subtotal</span><span>₹{fmtINR(form.subtotal)}</span></div>
                                            <div className="pg-total-row">
                                                <span className="pg-total-with-input">Tax <input type="number" name="taxPercentage" className="pg-input pg-input-mini" value={form.taxPercentage} onChange={handleChange} min="0" max="100" /> %</span>
                                                <span>₹{fmtINR(form.taxAmount)}</span>
                                            </div>
                                            <div className="pg-total-row">
                                                <span className="pg-total-with-input">Discount <input type="number" name="discount" className="pg-input pg-input-mini" value={form.discount} onChange={handleChange} min="0" style={{ width: '80px' }} /></span>
                                                <span>−₹{fmtINR(form.discount)}</span>
                                            </div>
                                            <div className="pg-total-row pg-total-final"><span>Total</span><span>₹{fmtINR(form.total)}</span></div>
                                        </div>
                                    </div>

                                    {/* Terms */}
                                    <div className="pg-form-section">
                                        <div className="pg-fs-header">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                            Payment & Terms
                                        </div>
                                        <div className="pg-field">
                                            <label className="pg-lbl">Terms & Conditions</label>
                                            <textarea name="termsAndConditions" className="pg-input pg-textarea" rows="6" value={form.termsAndConditions} onChange={handleChange} />
                                        </div>
                                        <div className="pg-field">
                                            <label className="pg-lbl">Additional Notes</label>
                                            <textarea name="notes" className="pg-input pg-textarea" rows="3" placeholder="Any special notes..." value={form.notes} onChange={handleChange} />
                                        </div>
                                    </div>

                                    {/* Signatures Upload */}
                                    <div className="pg-form-section">
                                        <div className="pg-fs-header">
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>
                                            Signatures
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                            {/* Authorized Signature */}
                                            <div className="pg-field">
                                                <label className="pg-lbl">Authorized Signatory</label>
                                                {authorizedSigPreview ? (
                                                    <div style={{ position: 'relative', display: 'inline-block', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '10px', background: '#fff' }}>
                                                        <img src={authorizedSigPreview} alt="Authorized" style={{ maxHeight: '70px', maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
                                                        <button type="button" onClick={() => removeSig('authorized')} style={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                                                    </div>
                                                ) : (
                                                    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '18px 12px', border: '2px dashed #cbd5e1', borderRadius: '10px', cursor: 'pointer', color: '#94a3b8', fontSize: '12px', transition: 'border-color .2s', background: '#fff' }} onMouseEnter={e => e.currentTarget.style.borderColor = '#3b775e'} onMouseLeave={e => e.currentTarget.style.borderColor = '#cbd5e1'}>
                                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                                        Upload Signature Image
                                                        <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={(e) => handleSigChange('authorized', e.target.files[0])} />
                                                    </label>
                                                )}
                                            </div>
                                            {/* Client Signature */}
                                            <div className="pg-field">
                                                <label className="pg-lbl">Client Signature</label>
                                                {clientSigPreview ? (
                                                    <div style={{ position: 'relative', display: 'inline-block', border: '1.5px solid #e2e8f0', borderRadius: '10px', padding: '10px', background: '#fff' }}>
                                                        <img src={clientSigPreview} alt="Client" style={{ maxHeight: '70px', maxWidth: '100%', objectFit: 'contain', display: 'block' }} />
                                                        {!readOnly && <button type="button" onClick={() => removeSig('client')} style={{ position: 'absolute', top: -8, right: -8, width: 22, height: 22, borderRadius: '50%', background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>}
                                                    </div>
                                                ) : (
                                                    <label style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '18px 12px', border: '2px dashed #cbd5e1', borderRadius: '10px', cursor: readOnly ? 'default' : 'pointer', color: '#94a3b8', fontSize: '12px', transition: 'border-color .2s', background: '#fff' }} onMouseEnter={e => !readOnly && (e.currentTarget.style.borderColor = '#3b775e')} onMouseLeave={e => !readOnly && (e.currentTarget.style.borderColor = '#cbd5e1')}>
                                                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                                                        Upload Signature Image
                                                        <input type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }} onChange={(e) => handleSigChange('client', e.target.files[0])} disabled={readOnly} />
                                                    </label>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    </fieldset>

                                    <div className="pg-form-footer" style={{ marginTop: '20px' }}>
                                        <button type="submit" className="pg-btn pg-btn-primary" disabled={saving}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                            {saving ? 'Saving...' : (proposalId ? 'Update Proposal' : 'Create Proposal')}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}



                        {/* RIGHT: Preview (always rendered, hidden if not active) */}
                        <div
                            className={`pg-preview-panel${activeTab === 'preview' ? ' pg-panel-visible' : ''}`}
                            style={{ 
                                height: '100%', 
                                overflowY: 'auto', 
                                padding: '0 24px 40px', 
                                background: '#f8fafc', 
                                display: readOnly || activeTab === 'preview' || window.innerWidth > 768 ? 'block' : 'none',
                                flex: readOnly ? 1 : undefined
                            }}
                        >
                            <div ref={previewRef} style={{ display: 'contents' }}>
                                <div className="pg-preview-toolbar" style={{ position: 'sticky', top: 0, zIndex: 10, background: '#f8fafc', padding: '12px 0' }}>
                                    <div className="pg-preview-label">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                        Live Preview
                                    </div>
                                    <div className="pg-zoom-controls">
                                        <button type="button" className="pg-zoom-btn" onClick={zoomOut} title="Zoom Out">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                                        </button>
                                        <span className="pg-zoom-level" onClick={zoomReset} title="Reset zoom">{Math.round(previewZoom * 100)}%</span>
                                        <button type="button" className="pg-zoom-btn" onClick={zoomIn} title="Zoom In">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                                        </button>
                                        <button type="button" className="pg-zoom-btn pg-expand-btn" onClick={() => setShowFullPreview(true)} title="Fullscreen Preview">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="pg-preview-zoom-area" style={{ transform: `scale(${previewZoom})`, transformOrigin: 'top center' }}>
                                    <div onClick={() => setShowFullPreview(true)} style={{ cursor: 'pointer' }}>
                                        {renderPreview()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Fullscreen Preview Modal */}
                        {showFullPreview && (
                            <div className="pg-fullpreview-overlay" onClick={() => setShowFullPreview(false)}>
                                <div className="pg-fullpreview-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="pg-fullpreview-header">
                                        <span className="pg-fullpreview-title">Proposal Preview</span>
                                        <button type="button" className="pg-fullpreview-close" onClick={() => setShowFullPreview(false)}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                        </button>
                                    </div>
                                    <div className="pg-fullpreview-body">
                                        {renderPreview()}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DomesticProposalGenerator;
